/*
 * Cookie / privacy consent state (GDPR).
 *
 * Model:
 *   • "necessary"  - always allowed (session id, theme preference, auth cookies).
 *   • "analytics"  - optional (page-view + event tracking to /api/analytics).
 *
 * Persisted in localStorage under `pr_consent_v1` as an object:
 *   { analytics: true|false, version: 1, ts: <ISO timestamp> }
 *
 * The pattern makes it trivial to add more buckets later (marketing, etc.)
 * without breaking existing acceptances.
 */

const STORAGE_KEY = "pr_consent_v1";
const CONSENT_VERSION = 1;

/** Bump this when the cookie policy changes materially so users re-consent. */
export const CURRENT_CONSENT_VERSION = CONSENT_VERSION;

const DEFAULTS = { analytics: false, version: CONSENT_VERSION };

let listeners = [];

export function getConsent() {
    if (typeof window === "undefined") return { ...DEFAULTS };
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { ...DEFAULTS, decided: false };
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.version !== CONSENT_VERSION) {
            return { ...DEFAULTS, decided: false };
        }
        return { ...DEFAULTS, ...parsed, decided: true };
    } catch {
        return { ...DEFAULTS, decided: false };
    }
}

export function setConsent(updates) {
    const current = getConsent();
    const next = {
        ...current,
        ...updates,
        version: CONSENT_VERSION,
        ts: new Date().toISOString(),
    };
    delete next.decided;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        /* ignore */
    }
    listeners.forEach((cb) => {
        try {
            cb(getConsent());
        } catch {
            /* ignore */
        }
    });
    return getConsent();
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function subscribeConsent(cb) {
    if (typeof cb !== "function") return () => {};
    listeners.push(cb);
    return () => {
        listeners = listeners.filter((c) => c !== cb);
    };
}

export function hasAnalyticsConsent() {
    const c = getConsent();
    return !!c.analytics;
}
